import { Button } from '@highlight/ui'
import {
  useOrganizationSkillDirectories,
  useSetOrganizationSkillSubscription,
  type OrganizationSkill,
} from '@renderer/chat/hooks/use-organization-skills'
import { useUser } from '@renderer/chat/store'
import { Skeleton } from '@renderer/components/ui/skeleton'
import { Settings } from '@renderer/windows/features/settings/components/settings'
import {
  useOrganizations,
  type SwitchableOrganization,
} from '@renderer/windows/features/people/hooks/use-organizations'
import { Check } from 'lucide-react'
import { useMemo } from 'react'
import { Modal, useModals } from '../index'
import {
  getOrganizationDirectoryState,
  getTeamOrganizations,
  isOrganizationSkillOwner,
} from './skills-marketplace-state'

interface OrganizationSectionProps {
  organization: SwitchableOrganization
  skills: Array<OrganizationSkill> | undefined
  isPending: boolean
  isError: boolean
  isSubscriptionPending: boolean
  pendingSkillId: string | null
  userId: string | null
  onRetry: () => void
  onSubscriptionChange: (skill: OrganizationSkill) => void
}

function DirectorySkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-[58px] w-full" />
      <Skeleton className="h-[58px] w-full" />
    </div>
  )
}

function OrganizationSection({
  organization,
  skills,
  isPending,
  isError,
  isSubscriptionPending,
  pendingSkillId,
  userId,
  onRetry,
  onSubscriptionChange,
}: OrganizationSectionProps) {
  const state = getOrganizationDirectoryState({ skills, isPending, isError })

  return (
    <section aria-labelledby={`organization-skills-${organization.organizationId}`} className="flex flex-col gap-2">
      <h3
        id={`organization-skills-${organization.organizationId}`}
        className="text-foreground-secondary text-sm font-medium"
      >
        {organization.name}
      </h3>

      {state.status === 'loading' ? (
        <DirectorySkeleton />
      ) : state.status === 'error' ? (
        <div className="text-foreground-secondary flex items-center justify-between gap-4 rounded-lg border p-4 text-sm">
          <span>Couldn&apos;t load this team&apos;s skills.</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : state.status === 'empty' ? (
        <div className="text-foreground-secondary rounded-lg border p-4 text-sm">No shared skills yet</div>
      ) : (
        <Settings.List>
          {state.skills.map((skill) => {
            const isPendingForSkill = pendingSkillId === skill.id
            const isOwner = isOrganizationSkillOwner(skill, userId)

            return (
              <Settings.ListItem key={skill.id}>
                <Settings.ListItemTitleGroup>
                  <Settings.ListItemTitle>{skill.name}</Settings.ListItemTitle>
                  <Settings.ListItemDescription>{skill.description}</Settings.ListItemDescription>
                </Settings.ListItemTitleGroup>
                <Settings.ListItemActionsGroup>
                  {isOwner ? (
                    <span className="text-foreground-secondary px-2 text-sm">Owner</span>
                  ) : (
                    <Button
                      variant={skill.subscribed ? 'outline' : 'primary'}
                      size="sm"
                      onClick={() => onSubscriptionChange(skill)}
                      disabled={isSubscriptionPending}
                      loading={isPendingForSkill}
                      left={skill.subscribed ? <Check /> : undefined}
                      aria-label={
                        skill.subscribed ? `Remove ${skill.name} from your skills` : `Add ${skill.name} to your skills`
                      }
                    >
                      {skill.subscribed ? 'Added' : 'Add'}
                    </Button>
                  )}
                </Settings.ListItemActionsGroup>
              </Settings.ListItem>
            )
          })}
        </Settings.List>
      )}
    </section>
  )
}

export const SkillsMarketplaceModal = () => {
  const { closeModal } = useModals()
  const user = useUser()
  const userId = user && !user.isAnonymous ? user.id : null
  const organizationsQuery = useOrganizations()
  const teamOrganizations = useMemo(
    () => getTeamOrganizations(organizationsQuery.data?.organizations ?? []),
    [organizationsQuery.data?.organizations],
  )
  const directoryQueries = useOrganizationSkillDirectories(
    teamOrganizations.map((organization) => organization.organizationId),
  )
  const setSubscription = useSetOrganizationSkillSubscription()

  const handleSubscriptionChange = (organizationId: string, skill: OrganizationSkill) => {
    setSubscription.reset()
    setSubscription.mutate({
      organizationId,
      skillId: skill.id,
      subscribed: !skill.subscribed,
    })
  }

  const pendingSkillKey = setSubscription.isPending
    ? `${setSubscription.variables.organizationId}:${setSubscription.variables.skillId}`
    : null

  const content = organizationsQuery.isPending ? (
    <div className="flex flex-col gap-5">
      <Skeleton className="h-5 w-36" />
      <DirectorySkeleton />
    </div>
  ) : organizationsQuery.isError ? (
    <div className="flex flex-col items-start gap-3 rounded-lg border p-5">
      <div>
        <p className="text-foreground text-sm font-medium">Couldn&apos;t load your teams</p>
        <p className="text-foreground-secondary mt-1 text-sm">Check your connection and try again.</p>
      </div>
      <Button variant="outline" size="sm" onClick={() => void organizationsQuery.refetch()}>
        Retry
      </Button>
    </div>
  ) : teamOrganizations.length === 0 ? (
    <div className="flex flex-col gap-1 rounded-lg border p-6 text-center">
      <p className="text-foreground text-sm font-medium">Join a team to explore shared skills</p>
      <p className="text-foreground-secondary text-sm">Team skills will appear here after you join an organization.</p>
    </div>
  ) : (
    <div className="flex flex-col gap-6">
      {setSubscription.isError && (
        <div role="alert" className="border-danger bg-danger-subtle text-danger-subtle rounded-lg border p-3 text-sm">
          Couldn&apos;t update this skill. Please try again.
        </div>
      )}
      {teamOrganizations.map((organization, index) => {
        const directoryQuery = directoryQueries[index]
        const keyPrefix = `${organization.organizationId}:`

        return (
          <OrganizationSection
            key={organization.organizationId}
            organization={organization}
            skills={directoryQuery.data}
            isPending={directoryQuery.isPending}
            isError={directoryQuery.isError}
            isSubscriptionPending={setSubscription.isPending}
            pendingSkillId={pendingSkillKey?.startsWith(keyPrefix) ? pendingSkillKey.slice(keyPrefix.length) : null}
            userId={userId}
            onRetry={() => void directoryQuery.refetch()}
            onSubscriptionChange={(skill) => handleSubscriptionChange(organization.organizationId, skill)}
          />
        )
      })}
    </div>
  )

  return (
    <Modal>
      <Modal.Content className="w-full max-w-[min(820px,92vw)]" showClose={false}>
        <Modal.Header>
          <Modal.Title>Team skills</Modal.Title>
          <Modal.Description>
            Add skills shared by your teams. Added skills stay connected to the team&apos;s latest version.
          </Modal.Description>
        </Modal.Header>

        <Modal.Body className="max-h-[60vh]">{content}</Modal.Body>

        <Modal.Footer>
          <Button variant="outline" onClick={() => closeModal('skills-marketplace')}>
            Done
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
